<template>
  <div class="flex items-center space-y-1 rounded-4 border">
    <slot name="drag-handle"></slot>
    <div class="space-y-1 p-4 flex-1">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <span class="flex items-center space-x-1 text-label">
            <span>@</span>
            <lf-h4>{{ whitelabel.domain_name }}</lf-h4>
          </span>
          <total-indicator
            v-tooltip="$t('COMMON.ALIAS', whitelabel.aliases_count)"
            :value="whitelabel.aliases_count"
          />
          <icon-base
            v-if="whitelabel.is_primary"
            :icon="IconPrimaryIndicator"
            class="text-blue-500"
          />
          <template v-if="isDefaultMailIntegrationType">
            <icon-base v-if="isSuccessStatus" :icon="IconSuccessBigGreen" />
            <lf-pill
              v-else-if="isPendingStatus"
              class="uppercase"
              :color-index="5"
            >
              {{ whitelabel.domain_identity_status }}
            </lf-pill>
            <icon-base
              v-else-if="whitelabel.domain_identity_status"
              class="rounded-full"
              :icon="IconFailBigRed"
            />
          </template>
          <icon-base v-else :icon="IconGoogleColored" />
        </div>
        <action-menu-wrapper
          v-if="whitelabel.id"
          :items="menuItems"
          :offset="offset"
          teleport
        />
      </div>
      <span v-if="whitelabel.updated_at" class="text-label">
        {{
          $t("EMAIL_INTEGRATION.LAST_UPDATED", {
            date: formatDateCustom(
              whitelabel.updated_at,
              "MM.dd.yyyy hh:mm a",
              true
            ),
          })
        }}
      </span>
      <confirm-modal
        v-if="showDeleteModal"
        :title="`${$t('COMMON.DELETE')} ${$t('COMMON.DOMAIN')}`"
        :description="
          $t('EMAIL_INTEGRATION.DELETE_ITEM_PROMPT', {
            item: whitelabel.domain_name || $t('COMMON.DOMAIN'),
          })
        "
        :confirm-button="$t('COMMON.DELETE')"
        :close="() => (showDeleteModal = false)"
        confirm-button-type="error"
        @confirm="deleteWhitelabel"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import compact from "lodash/compact";

import { MailIntegrationType } from "@/enums/communicationLogs";
import { formatDateCustom } from "@/helpers/formatting";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import clientsApiService from "@/services/modules/clients";

import TotalIndicator from "@/components/deals/dataServices/cfa/moneythumb/TotalIndicator.vue";
import IconFailBigRed from "@/components/icons/IconFailBigRed.vue";
import IconGoogleColored from "@/components/icons/IconGoogleColored.vue";
import IconPrimaryIndicator from "@/components/icons/IconPrimaryIndicator.vue";
import IconSuccessBigGreen from "@/components/icons/IconSuccessBigGreen.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfPill from "@/components/ui/text/LfPill.vue";

import type { IClient, IWhitelabel } from "@/models/clients";

const { whitelabel } = defineProps<{ whitelabel: IWhitelabel }>();

const emit = defineEmits<{
  "whitelabel:delete": [];
}>();

const { t } = useI18n();
const { activeClient } = useClients();
const { dispatch, getters } = useStore();
const { showMessage } = useNotification();
const router = useRouter();

const authClientId = computed(
  () => (getters["auth/authClientSettings"] as IClient)?.["id"] || undefined
);

const routeClientId = computed(() => {
  return router.currentRoute.value.params.id as string | undefined;
});

const isDefaultMailIntegrationType = computed(
  () => whitelabel.mail_integration_type === MailIntegrationType.Default
);

const offset = {
  left: authClientId.value && whitelabel.id ? -140 : -65,
  top: 5,
};

const showDeleteModal = ref(false);

const isSuccessStatus = computed(
  () =>
    whitelabel.domain_identity_status?.toLowerCase() ===
    t("COMMON.SUCCESS").toLowerCase()
);
const isPendingStatus = computed(
  () =>
    whitelabel.domain_identity_status?.toLowerCase() ===
    t("COMMON.PENDING").toLowerCase()
);

const menuItems = computed<Array<MenuItem>>(() =>
  compact([
    {
      label: t("COMMON.DELETE"),
      handler: () => (showDeleteModal.value = true),
    },
    isDefaultMailIntegrationType.value && {
      label: t("COMMON.REFRESH"),
      handler: async () => refreshStatus(),
    },
    (authClientId.value || routeClientId.value) &&
      whitelabel.id &&
      isDefaultMailIntegrationType.value && {
        label: t("EMAIL_INTEGRATION.VIEW_DNS_RECORDS"),
        handler: () =>
          router.push({
            name: "WhitelabelView",
            params: {
              clientId: authClientId.value || routeClientId.value,
              whitelabelId: whitelabel.id,
            },
          }),
      },
  ])
);

const { fetchWrapper: refreshStatus } = usePromiseWrapper(async () => {
  if (!whitelabel.id) {
    return;
  }
  await dispatch("clients/getDomainStatus", whitelabel.id);
  showMessage(t("COMMON.STATUS_REFRESHED"), "success");
});

const { fetchWrapper: deleteWhitelabel } = usePromiseWrapper(async () => {
  if (whitelabel.id && activeClient.value) {
    await clientsApiService.deleteDns(activeClient.value?.id, whitelabel.id);
    showDeleteModal.value = false;
    emit("whitelabel:delete");
  }
});
</script>
